/**
 * Admin: Admin team management (Owner only).
 *  - Lists current admins (Owner + Editors)
 *  - Send invite by email (creates one-time link, sends via Resend if configured)
 *  - Revoke pending invite
 *  - Remove an existing Editor admin
 */
import React, { useEffect, useState } from 'react';
import { Copy, Mail, Shield, Trash2, UserPlus } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [lastInvite, setLastInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const [a, i] = await Promise.all([
        api.get('/admin/admins'),
        api.get('/admin/invites'),
      ]);
      setAdmins(a.data.items || []);
      setInvites(i.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load admin team');
    }
  }

  useEffect(() => { load(); }, []);

  async function sendInvite(e) {
    e.preventDefault();
    setErr(''); setLastInvite(null); setBusy('invite');
    try {
      const r = await api.post('/admin/invites', { email: email.trim().toLowerCase(), name: name.trim() });
      setLastInvite(r.data);
      setEmail(''); setName('');
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not send invite');
    } finally { setBusy(''); }
  }

  async function revokeInvite(id) {
    if (!window.confirm('Revoke this invite? The link will stop working immediately.')) return;
    setBusy(`revoke-${id}`);
    try { await api.delete(`/admin/invites/${id}`); await load(); }
    finally { setBusy(''); }
  }

  async function removeAdmin(a) {
    if (!window.confirm(`Remove ${a.email} from the admin team? They'll lose access immediately.`)) return;
    setBusy(`rm-${a.id}`);
    try { await api.delete(`/admin/admins/${a.id}`); await load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not remove admin'); }
    finally { setBusy(''); }
  }

  function copyInviteLink(link) {
    try {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) { /* clipboard unsupported — silent fail */ }
  }

  const pendingInvites = invites.filter((i) => i.status === 'pending' && !i.expired);
  const inactiveInvites = invites.filter((i) => !(i.status === 'pending' && !i.expired));

  return (
    <div data-testid="admin-admins-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-display text-3xl sm:text-4xl mb-2">Admin team</h1>
        <p className="text-muted text-sm sm:text-base">Invite other people to help run the site. Invitees get a one-time email link to set their own password.</p>
      </div>

      <HelpCallout
        title="How invites work"
        description="You're the workspace Owner — only you can invite or remove admins. Invitees become 'Editors' and can do everything except manage the admin team itself. Invite links last 7 days. Emails are sent from alerts@goleicestershire.com via Resend (or you can copy the link manually below if you'd rather send it yourself)."
      />

      {/* Invite form */}
      <section className="bg-white rounded-2xl border border-charcoal/10 p-5 sm:p-6 mt-6">
        <h2 className="font-display text-xl mb-4 flex items-center gap-2"><UserPlus size={18} /> Invite a new admin</h2>
        <form onSubmit={sendInvite} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="admin-invite-form">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-charcoal/60">Email</span>
            <input data-testid="admin-invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              className="mt-1 w-full border border-charcoal/15 rounded-xl px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-charcoal/60">Name (optional)</span>
            <input data-testid="admin-invite-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="mt-1 w-full border border-charcoal/15 rounded-xl px-3 py-2 text-sm" />
          </label>
          <button type="submit" disabled={busy === 'invite'} data-testid="admin-invite-submit"
            className="bg-forest text-base rounded-xl px-5 py-2.5 text-sm font-semibold hover:bg-heritage disabled:opacity-50 flex items-center gap-2">
            <Mail size={14} /> {busy === 'invite' ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        {err && <div className="mt-3 text-sm text-heritage" data-testid="admin-invite-error">{err}</div>}
        {lastInvite && (
          <div className="mt-4 p-4 bg-mustard/15 border border-mustard rounded-xl text-sm" data-testid="admin-invite-success">
            <div className="font-semibold mb-1">Invite created for {lastInvite.email}</div>
            <div className="text-charcoal/70 mb-2">
              {lastInvite.email_status?.ok && 'Email delivered. They have 7 days to accept.'}
              {!lastInvite.email_status?.ok && lastInvite.email_status?.skipped &&
                'No email provider configured — copy the link below and share it manually.'}
              {!lastInvite.email_status?.ok && !lastInvite.email_status?.skipped && (
                <span><strong>Email send failed</strong> ({lastInvite.email_status?.error_message || lastInvite.email_status?.error_name || lastInvite.email_status?.status || lastInvite.email_status?.error || 'unknown error'}). The invite is still valid — copy the link below and share it manually.</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-white px-2 py-1.5 rounded border border-charcoal/10 break-all flex-1 min-w-0">{lastInvite.accept_link}</code>
              <button onClick={() => copyInviteLink(lastInvite.accept_link)} data-testid="admin-invite-copy-link"
                className="text-xs bg-white border border-charcoal/15 hover:bg-admin rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
                <Copy size={12} /> {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Current admins */}
      <section className="bg-white rounded-2xl border border-charcoal/10 p-5 sm:p-6 mt-6">
        <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Shield size={18} /> Current admins ({admins.length})</h2>
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-charcoal/50">
              <tr>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">Role</th>
                <th className="py-2 px-3 hidden md:table-cell">Joined</th>
                <th className="py-2 px-3 hidden md:table-cell">Last sign-in</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id || a.email} className="border-t border-charcoal/5" data-testid={`admin-row-${a.email}`}>
                  <td className="py-3 px-3 break-all">{a.email}</td>
                  <td className="py-3 px-3">
                    {a.role === 'owner'
                      ? <span className="text-heritage text-xs uppercase tracking-wide font-bold">Owner</span>
                      : <span className="text-charcoal/70 text-xs uppercase tracking-wide">Editor</span>}
                  </td>
                  <td className="py-3 px-3 text-charcoal/60 hidden md:table-cell">{fmtDate(a.created_at)}</td>
                  <td className="py-3 px-3 text-charcoal/60 hidden md:table-cell">{fmtDate(a.last_login)}</td>
                  <td className="py-3 px-3 text-right">
                    {a.role !== 'owner' && (
                      <button onClick={() => removeAdmin(a)} disabled={busy === `rm-${a.id}`}
                        data-testid={`admin-remove-${a.email}`}
                        className="text-heritage hover:underline text-xs inline-flex items-center gap-1 disabled:opacity-50">
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pending invites */}
      <section className="bg-white rounded-2xl border border-charcoal/10 p-5 sm:p-6 mt-6">
        <h2 className="font-display text-xl mb-4">Pending invites ({pendingInvites.length})</h2>
        {pendingInvites.length === 0 ? (
          <div className="text-sm text-muted" data-testid="admin-no-pending-invites">No outstanding invites.</div>
        ) : (
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-charcoal/50">
                <tr>
                  <th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3 hidden sm:table-cell">Sent</th>
                  <th className="py-2 px-3 hidden sm:table-cell">Expires</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((i) => (
                  <tr key={i.id} className="border-t border-charcoal/5" data-testid={`pending-invite-${i.email}`}>
                    <td className="py-3 px-3 break-all">{i.email}{i.name && <span className="text-charcoal/50"> · {i.name}</span>}</td>
                    <td className="py-3 px-3 text-charcoal/60 hidden sm:table-cell">{fmtDate(i.created_at)}</td>
                    <td className="py-3 px-3 text-charcoal/60 hidden sm:table-cell">{fmtDate(i.expires_at)}</td>
                    <td className="py-3 px-3 text-right">
                      <button onClick={() => revokeInvite(i.id)} disabled={busy === `revoke-${i.id}`}
                        data-testid={`revoke-invite-${i.email}`}
                        className="text-heritage hover:underline text-xs inline-flex items-center gap-1 disabled:opacity-50">
                        <Trash2 size={12} /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* History (expired / consumed / revoked) */}
      {inactiveInvites.length > 0 && (
        <section className="bg-white rounded-2xl border border-charcoal/10 p-5 sm:p-6 mt-6">
          <h2 className="font-display text-base mb-3 text-charcoal/70">History</h2>
          <ul className="text-xs text-charcoal/60 space-y-1">
            {inactiveInvites.slice(0, 10).map((i) => (
              <li key={i.id} className="flex flex-wrap gap-2">
                <span className="font-medium text-charcoal/80">{i.email}</span>
                <span className="text-charcoal/50">{i.status === 'consumed' ? 'accepted' : (i.expired ? 'expired' : i.status)}</span>
                <span>· {fmtDate(i.consumed_at || i.revoked_at || i.expires_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
